import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useAuthConfirmEmail } from "@/orval/auth";
import { useAuth } from "@/hooks/auth/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { z } from "zod";

const confirmationSearchSchema = z.object({
  email: z.string().catch(""),
  token: z.string().catch(""),
});

const successMessage = "Your email address has been confirmed. You can now sign in.";

const ConfirmEmail = () => {
  const { email, token } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthenticated, setToken } = useAuth();
  const { mutateAsync: confirmEmail, isPending } = useAuthConfirmEmail();
  const [shouldRedirectToOrganizations, setShouldRedirectToOrganizations] = useState(false);

  const goToLogin = () => {
    navigate({ to: "/login", search: { redirect: undefined, message: successMessage, messageType: "info" } });
  };

  const handleConfirm = async () => {
    const result = await confirmEmail({ data: { email, token } });

    if (!result.token) {
      goToLogin();
      return;
    }

    setToken(result.token);
    setShouldRedirectToOrganizations(true);
  };

  useLayoutEffect(() => {
    if (shouldRedirectToOrganizations && isAuthenticated) {
      navigate({ to: "/organizations" });
    }
  }, [isAuthenticated, navigate, shouldRedirectToOrganizations]);

  if (!email || !token) {
    return (
      <div className="flex items-center justify-center flex-1 p-4">
        <Card className="max-w-full w-96">
          <CardHeader>
            <CardTitle>Confirmation link is incomplete</CardTitle>
            <CardDescription>Please request a new email confirmation link and try again.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-1 p-4">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>Confirm your email</CardTitle>
        </CardHeader>
        <CardFooter className="p-6 pt-0">
          <IconButton className="w-full" icon={<CheckCircle2 />} onClick={handleConfirm} disabled={isPending}>
            {isPending ? "Confirming..." : "Confirm Email"}
          </IconButton>
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/confirm-email")({
  validateSearch: confirmationSearchSchema,
  component: ConfirmEmail,
});
